<template>
  <div class="lead-settings">
    <div class="settings-wrapper">
      <div v-if="activeTab === 'statuses'">
        <div class="d-flex justify-content-between mb-3 align-items-start gap-3">
          <div>
            <h6 class="mb-1">{{ $t('lead_statuses') }}</h6>
            <p class="text-muted small mb-0">{{ $t('manage_lead_statuses_desc') }}</p>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary flex-shrink-0" @click="addStatus">
            <AppIcon name="bi-plus" class="bi bi-plus me-1" />{{ $t('add_status') }}
          </button>
        </div>

        <div class="p-4 mb-6 bg-white rounded shadow-sm border">
          <div v-if="loading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>{{ $t('loading') }}...
          </div>

          <div v-else>
            <div class="alert alert-light border small text-muted py-2 mb-4">
              <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />{{ $t('status_changes_help') }}
            </div>

            <div
              v-for="(status, index) in editStatuses"
              :key="status.id || status._key"
              class="status-row mb-3"
            >
              <div class="status-preview" :style="{ backgroundColor: status.color }" aria-hidden="true"></div>
              <input
                type="color"
                v-model="status.color"
                class="form-control form-control-color status-color"
                :aria-label="$t('status_color')"
              >
              <div class="flex-grow-1">
                <input
                  type="text"
                  v-model="status.name"
                  class="form-control"
                  :class="{ 'is-invalid': statusNameErrors[index] }"
                  :placeholder="$t('status_name')"
                  maxlength="255"
                >
                <div v-if="statusNameErrors[index]" class="invalid-feedback">
                  {{ statusNameErrors[index] }}
                </div>
              </div>
              <button
                type="button"
                class="btn btn-outline-danger flex-shrink-0"
                @click="removeStatus(index)"
                :disabled="editStatuses.length <= 1"
                :title="$t('delete')"
              >
                <AppIcon name="bi-trash" class="bi bi-trash" />
              </button>
            </div>

            <div class="mt-4 d-flex justify-content-end gap-2">
              <button
                type="button"
                class="btn btn-light"
                :disabled="savingStatuses || !hasStatusChanges"
                @click="resetStatuses"
              >
                {{ $t('reset') }}
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="savingStatuses || !hasStatusChanges || hasStatusErrors"
                @click="saveStatuses"
              >
                <span v-if="savingStatuses" class="spinner-border spinner-border-sm me-2"></span>
                <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />{{ $t('save') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'sources'">
        <div class="d-flex justify-content-between mb-3 align-items-start gap-3">
          <div>
            <h6 class="mb-1">{{ $t('lead_sources') }}</h6>
            <p class="text-muted small mb-0">{{ $t('manage_lead_sources_desc') }}</p>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary flex-shrink-0" @click="addSource">
            <AppIcon name="bi-plus" class="bi bi-plus me-1" />{{ $t('add_source') }}
          </button>
        </div>

        <div class="p-4 mb-6 bg-white rounded shadow-sm border">
          <div class="alert alert-light border small text-muted py-2 mb-4">
            <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />{{ $t('source_changes_help') }}
          </div>

          <div v-if="editSources.length">
            <div v-for="(source, index) in editSources" :key="source._key" class="status-row mb-3">
              <div class="flex-grow-1">
                <input
                  v-model="source.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': sourceNameErrors[index] }"
                  :placeholder="$t('source')"
                  maxlength="255"
                >
                <div v-if="sourceNameErrors[index]" class="invalid-feedback">{{ sourceNameErrors[index] }}</div>
              </div>
              <button type="button" class="btn btn-outline-danger flex-shrink-0" :title="$t('delete')" @click="removeSource(index)">
                <AppIcon name="bi-trash" class="bi bi-trash" />
              </button>
            </div>
          </div>
          <div v-else class="text-center text-muted py-4">{{ $t('no_lead_sources_configured') }}</div>

          <div class="mt-4 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-light" :disabled="savingSources || !hasSourceChanges" @click="resetSources">
              {{ $t('reset') }}
            </button>
            <button type="button" class="btn btn-success" :disabled="savingSources || !hasSourceChanges || hasSourceErrors" @click="saveSources">
              <span v-if="savingSources" class="spinner-border spinner-border-sm me-2"></span>
              <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />{{ $t('save') }}
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'columns'">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-4">
              <div>
                <h6 class="card-title mb-1">
                  <AppIcon name="bi-table" class="bi bi-table me-2" />{{ $t('leads_table_columns') }}
                </h6>
                <p class="text-muted small mb-0">{{ $t('table_columns_description') }}</p>
              </div>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="savingColumns"
                @click="resetColumns"
              >
                <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />{{ $t('reset') }}
              </button>
            </div>

            <div class="row g-3">
              <div v-for="(isVisible, key) in visibleColumns" :key="key" class="col-md-4 col-sm-6">
                <div class="form-check form-switch">
                  <input
                    :id="`lead-column-${key}`"
                    v-model="visibleColumns[key]"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="savingColumns"
                    @change="saveColumnSettings"
                  >
                  <label class="form-check-label" :for="`lead-column-${key}`">
                    {{ $t(getColumnLabel(key)) }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'rules'">
        <div class="card">
          <div class="card-body">
            <div class="mb-4">
              <h6 class="card-title mb-1">
                <AppIcon name="bi-sliders" class="bi bi-sliders me-2" />{{ $t('lead_rules') }}
              </h6>
              <p class="text-muted small mb-0">{{ $t('lead_rules_description') }}</p>
            </div>

            <div class="mb-4">
              <label class="form-label fw-semibold" for="lead-phone-format">{{ $t('phone_format') }}</label>
              <select id="lead-phone-format" v-model="leadRules.phone_format" class="form-select">
                <option value="as_entered">{{ $t('phone_format_as_entered') }}</option>
                <option value="digits_only">{{ $t('phone_format_digits_only') }}</option>
                <option value="international">{{ $t('phone_format_international') }}</option>
              </select>
              <div class="form-text">{{ $t('phone_format_help') }}</div>
            </div>

            <div v-if="leadRules.phone_format === 'international'" class="mb-4">
              <label class="form-label fw-semibold" for="lead-country-code">{{ $t('default_country_code') }}</label>
              <input
                id="lead-country-code"
                v-model.trim="leadRules.phone_country_code"
                type="text"
                class="form-control"
                placeholder="+212"
                maxlength="8"
              >
              <div class="form-text">{{ $t('default_country_code_help') }}</div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-semibold d-block">{{ $t('unique_lead_fields') }}</label>
              <p class="text-muted small">{{ $t('unique_lead_fields_help') }}</p>
              <div class="row g-3">
                <div v-for="field in ruleFields" :key="field" class="col-md-6">
                  <div class="form-check form-switch">
                    <input
                      :id="`unique-lead-${field}`"
                      v-model="leadRules.unique_fields"
                      :value="field"
                      class="form-check-input"
                      type="checkbox"
                    >
                    <label class="form-check-label" :for="`unique-lead-${field}`">{{ $t(field) }}</label>
                  </div>
                </div>
              </div>
              <div class="alert alert-light border small text-muted py-2 mt-3 mb-0">
                <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />{{ $t('lead_rules_existing_data_help') }}
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-light" :disabled="savingRules || !hasRuleChanges" @click="resetRules">
                {{ $t('reset') }}
              </button>
              <button type="button" class="btn btn-success" :disabled="savingRules || !hasRuleChanges" @click="saveRules">
                <span v-if="savingRules" class="spinner-border spinner-border-sm me-2"></span>
                <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />{{ $t('save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';
import {
  LEAD_TABLE_COLUMN_LABELS,
  getLeadTableColumnDefaults
} from '../../constants/leadTableColumns';

export default {
  name: 'LeadSettings',
  props: ['activeTab'],
  data() {
    return {
      editStatuses: [],
      initialStatuses: [],
      loading: false,
      savingStatuses: false,
      savingSources: false,
      savingColumns: false,
      savingRules: false,
      visibleColumns: getLeadTableColumnDefaults(),
      editSources: [],
      initialSources: [],
      ruleFields: ['name', 'email', 'phone', 'company'],
      leadRules: this.defaultRules(),
      initialRules: this.defaultRules()
    };
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS;
    },
    statusNameErrors() {
      const names = this.editStatuses.map(status => (status.name || '').trim().toLocaleLowerCase());

      return this.editStatuses.map((status, index) => {
        if (!(status.name || '').trim()) return this.$t('status_name_required');
        if (names.some((name, otherIndex) => name === names[index] && otherIndex !== index)) {
          return this.$t('duplicate_status_name');
        }
        return '';
      });
    },
    hasStatusErrors() {
      return this.statusNameErrors.some(Boolean);
    },
    hasStatusChanges() {
      return JSON.stringify(this.statusPayload(this.editStatuses)) !== JSON.stringify(this.statusPayload(this.initialStatuses));
    },
    sourceNameErrors() {
      const names = this.editSources.map(source => (source.name || '').trim().toLocaleLowerCase());
      return this.editSources.map((source, index) => {
        if (!names[index]) return this.$t('source_name_required');
        if (names.some((name, otherIndex) => name === names[index] && otherIndex !== index)) {
          return this.$t('duplicate_source_name');
        }
        return '';
      });
    },
    hasSourceErrors() {
      return this.sourceNameErrors.some(Boolean);
    },
    hasSourceChanges() {
      return JSON.stringify(this.sourcePayload(this.editSources)) !== JSON.stringify(this.sourcePayload(this.initialSources));
    },
    hasRuleChanges() {
      return JSON.stringify(this.normalizedRules(this.leadRules)) !== JSON.stringify(this.normalizedRules(this.initialRules));
    }
  },
  mounted() {
    if (this.activeTab === 'statuses') {
      this.loadStatuses();
    } else if (this.activeTab === 'sources') {
      this.loadSources();
    } else if (this.activeTab === 'columns') {
      this.loadColumnSettings();
    } else if (this.activeTab === 'rules') {
      this.loadRules();
    }
  },
  watch: {
    activeTab(newTab) {
        if (newTab === 'statuses') {
            this.loadStatuses();
        } else if (newTab === 'sources') {
            this.loadSources();
        } else if (newTab === 'columns') {
            this.loadColumnSettings();
        } else if (newTab === 'rules') {
            this.loadRules();
        }
    }
  },
  methods: {
    sourcePayload(sources) {
      return sources.map(source => (source.name || '').trim());
    },
    setSources(sources) {
      const mapped = (Array.isArray(sources) ? sources : []).map((source, index) => ({
        _key: `source_${Date.now()}_${index}`,
        name: source || ''
      }));
      this.editSources = mapped;
      this.initialSources = mapped.map(source => ({ ...source }));
    },
    loadSources() {
      this.setSources(this.business?.metadata?.lead_sources || []);
    },
    addSource() {
      this.editSources.push({
        _key: `source_${Date.now()}_${this.editSources.length}`,
        name: ''
      });
    },
    removeSource(index) {
      this.editSources.splice(index, 1);
    },
    resetSources() {
      this.editSources = this.initialSources.map(source => ({ ...source }));
    },
    async saveSources() {
      if (this.hasSourceErrors || !this.hasSourceChanges) return;
      try {
        this.savingSources = true;
        const leadSources = this.sourcePayload(this.editSources);
        const metadata = {
          ...(this.business?.metadata || {}),
          lead_sources: leadSources
        };
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', { ...this.business, metadata });
        this.setSources(leadSources);
      } finally {
        this.savingSources = false;
      }
    },
    defaultRules() {
      return {
        phone_format: 'as_entered',
        phone_country_code: '',
        unique_fields: ['email', 'phone']
      };
    },
    normalizedRules(rules) {
      return {
        phone_format: ['as_entered', 'digits_only', 'international'].includes(rules?.phone_format)
          ? rules.phone_format
          : 'as_entered',
        phone_country_code: (rules?.phone_country_code || '').trim(),
        unique_fields: this.ruleFields.filter(field => (rules?.unique_fields || []).includes(field))
      };
    },
    loadRules() {
      const rules = this.normalizedRules({
        ...this.defaultRules(),
        ...(this.business?.metadata?.lead_rules || {})
      });
      this.leadRules = { ...rules, unique_fields: [...rules.unique_fields] };
      this.initialRules = { ...rules, unique_fields: [...rules.unique_fields] };
    },
    resetRules() {
      this.leadRules = { ...this.initialRules, unique_fields: [...this.initialRules.unique_fields] };
    },
    async saveRules() {
      if (!this.hasRuleChanges) return;
      try {
        this.savingRules = true;
        const leadRules = this.normalizedRules(this.leadRules);
        const metadata = {
          ...(this.business?.metadata || {}),
          lead_rules: leadRules
        };
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', { ...this.business, metadata });
        this.leadRules = { ...leadRules, unique_fields: [...leadRules.unique_fields] };
        this.initialRules = { ...leadRules, unique_fields: [...leadRules.unique_fields] };
      } finally {
        this.savingRules = false;
      }
    },
    getColumnLabel(key) {
      return LEAD_TABLE_COLUMN_LABELS[key] || key;
    },
    loadColumnSettings() {
      this.visibleColumns = {
        ...getLeadTableColumnDefaults(),
        ...(this.business?.metadata?.leads_table_columns || {})
      };
    },
    async saveColumnSettings() {
      try {
        this.savingColumns = true;
        const metadata = {
          ...(this.business?.metadata || {}),
          leads_table_columns: { ...this.visibleColumns }
        };

        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        );

        this.$store.commit('SET_BUSINESS', {
          ...this.business,
          metadata
        });
      } catch (e) {
        this.loadColumnSettings();
      } finally {
        this.savingColumns = false;
      }
    },
    async resetColumns() {
      this.visibleColumns = getLeadTableColumnDefaults();
      await this.saveColumnSettings();
    },
    async loadStatuses() {
      try {
        this.loading = true;
        const res = await helper.customGet(`${process.env.VUE_APP_API}/lead-statuses`);
        this.setStatuses(res.data.length ? res.data : [{ name: 'New', color: '#6c757d' }]);
      } catch (e) {
        this.editStatuses = [];
        this.initialStatuses = [];
      } finally {
        this.loading = false;
      }
    },
    setStatuses(statuses) {
      const mappedStatuses = statuses.map((status, index) => ({
        id: status.id || null,
        _key: status.id || `status_${Date.now()}_${index}`,
        name: status.name || '',
        color: status.color || '#6c757d'
      }));
      this.editStatuses = mappedStatuses;
      this.initialStatuses = mappedStatuses.map(status => ({ ...status }));
    },
    statusPayload(statuses) {
      return statuses.map(status => ({
        ...(status.id ? { id: status.id } : {}),
        name: (status.name || '').trim(),
        color: status.color || '#6c757d'
      }));
    },
    addStatus() {
      this.editStatuses.push({
        id: null,
        _key: `status_${Date.now()}_${this.editStatuses.length}`,
        name: '',
        color: '#6c757d'
      });
    },
    removeStatus(index) {
      this.editStatuses.splice(index, 1);
    },
    resetStatuses() {
      this.editStatuses = this.initialStatuses.map(status => ({ ...status }));
    },
    async saveStatuses() {
      if (this.hasStatusErrors || !this.hasStatusChanges) return;

      try {
        this.savingStatuses = true;
        const response = await helper.customPost(
          `${process.env.VUE_APP_API}/lead-statuses`,
          { statuses: this.statusPayload(this.editStatuses) },
          { showToast: true }
        );
        this.setStatuses(response.data);
        this.$emit('statuses-updated', response.data);
      } catch (e) {
        // Errors are displayed by the shared request helper.
      } finally {
        this.savingStatuses = false;
      }
    }
  }
};
</script>

<style scoped>
.lead-settings {
  padding: 0;
}
.lead-settings .card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.lead-settings .card-title {
  color: #333;
  font-size: 1rem;
  font-weight: 600;
}
.settings-wrapper {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
}
.status-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.status-preview {
  width: 10px;
  height: 38px;
  border-radius: 0.375rem;
  flex-shrink: 0;
}
.status-color {
  width: 48px;
  height: 38px;
  padding: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
}
</style>
